// animalAddendumForm — DEPENDENCY-FREE pure helpers for the WEL-153 Animal
// Addendum intake form (the new-animal-on-a-current-tenancy document, spec
// docs/animal-addendum.md).
//
// No network, no `@/` imports, no React — so the draft shape and every
// validation rule stay unit-testable with a bare `npx tsx --test` (the same
// split lib/moveoutFundsForm.ts makes, for the same reason). The client module
// re-exports these so app call sites can import from one place. The imports
// (./animalQuestions, ./animalDraft, ./money) are themselves dependency-free.
//
// WHAT is asked comes from the WEL-173 question matrix (lib/animalQuestions.ts),
// shared with the pre-lease intake form and the API: the household picks what
// kind of animal it is (Dog, Cat, Bird, Small caged animal, Reptile,
// Fish / aquarium, Something else) and is asked only that kind's questions — a
// dog its breed, weight and age; a cat its age and color; a hamster only what
// kind it is. A license is never required. The API holds a submission to the
// same required answers (it carries `questionsVersion`).
//
// Ids: every new animal carries a client-generated id so a photo upload can be
// attached to the right animal before the submission exists server-side. It is
// an addressing value only — it is never rendered as text (docs/no-ids-in-ui.md).

import { formatMoneyCents } from './money';
import { ANIMAL_QUESTIONS_VERSION, type AnimalField, type OtherAnimalKind } from './animalQuestions';
import {
  animalAnswerReasons,
  asksDraftField,
  blankAnimalAnswers,
  draftToWireAnswers,
  storedToAnimalAnswers,
  type AnimalAnswerDraft,
  type AnimalWireAnswers,
} from './animalDraft';

/** The three stored animal types (mirrors the intake form's AnimalType). */
export type AnimalAddendumAnimalType = 'dog' | 'cat' | 'other';

/** A photo the tenant attached to one new animal (returned by the upload route). */
export interface AnimalAddendumPhoto {
  /** Server-side photo id — addressing only, never displayed. */
  id: string;
  contentType: string;
  bytes: number;
  /** Short-lived presigned GET the form renders as the thumbnail. */
  previewUrl: string;
}

/**
 * One NEW animal as the form collects it: the shared per-animal answers
 * (lib/animalDraft.ts — the same shape the pre-lease intake edits) plus the
 * client id and the optional photo. The numeric and enum narrowing happens
 * once, in `buildAnimalAddendumSubmission`.
 */
export interface AnimalAddendumDraftAnimal extends AnimalAnswerDraft {
  /** Client-generated id (see the module note). Addressing only. */
  id: string;
  /** Set once an upload succeeds; cleared client-side by "Remove". */
  photo?: AnimalAddendumPhoto;
  /**
   * RRA v2.2 part B: a COVERED animal — listed by the server (it prices the round at no additional
   * animal rent or deposit for it). What the server already knows (name, type, and a breed or other kind)
   * is prefilled; a name or type it fixed is locked so the answers stay paired with it, and the
   * household completes the rest. It cannot be removed, and it is submitted under the id the server gave
   * it. A field the server left blank stays editable (the lock is decided once, when the row is seeded).
   * Absent on every animal the household adds.
   */
  covered?: { lockName: boolean; lockKind: boolean };
}

/**
 * One covered animal as the server lists it on the form view (GET intake/:token `coveredAnimals`).
 * `id` pairs the submitted row with it; the rest is whatever the server already knows.
 */
export interface AnimalAddendumCoveredAnimal {
  id: string;
  name?: string;
  type?: AnimalAddendumAnimalType;
  otherKind?: OtherAnimalKind;
  otherDescription?: string;
  breed?: string;
  /**
   * WEL-182: staff marked this animal "pending RRA" on the Send page. It is $0 now, and the
   * addendum prints the lease's neutral pending-decision clause with these if-not-approved
   * amounts (per animal; the clause states the totals). Absent = no additional charge, full stop
   * (marked "RRA" at send, or covered by an RRA v2.2 approval). Never rendered with a reason.
   */
  pendingDecision?: { ifNotApprovedRentCents: number; ifNotApprovedDepositCents: number };
}

/**
 * WEL-182: the household's line for a covered animal whose charge is not settled — $0 now, and what
 * applies if it stays that way. DRAFT wording, the same sentence the intake email carries (W3 §2):
 *
 *   "Rex — $0 for now. If we don't waive it, $50.00 a month in animal rent and a $500.00 deposit
 *    apply from the first of the month after our written notice."
 *
 * It says nothing about why (Rule H): no decision, no request, no accommodation is named. Amounts are
 * this animal's own; the addendum itself states the totals.
 */
export function pendingAnimalLine(
  name: string | undefined,
  pending: NonNullable<AnimalAddendumCoveredAnimal['pendingDecision']>,
): string {
  const who = name?.trim() || 'This animal';
  return (
    `${who} — $0 for now. If we don't waive it, ` +
    `${formatMoneyCents(pending.ifNotApprovedRentCents)} a month in animal rent and a ` +
    `${formatMoneyCents(pending.ifNotApprovedDepositCents)} deposit apply from the first of the month ` +
    `after our written notice.`
  );
}

/** The whole form draft: one or more new animals. */
export interface AnimalAddendumDraft {
  animals: AnimalAddendumDraftAnimal[];
}

/** One new animal as it goes on the wire (POST /api/animal-addendum/intake/:token). */
export interface AnimalAddendumSubmissionAnimal extends AnimalWireAnswers {
  id: string;
  type: AnimalAddendumAnimalType;
}

/** The submit body. */
export interface AnimalAddendumSubmission {
  animals: AnimalAddendumSubmissionAnimal[];
  /** Tells the API this was built from the WEL-173 matrix (see animalQuestions). */
  questionsVersion: number;
}

/**
 * A fresh client id for a new animal row.
 *
 * `crypto.randomUUID` where it exists (every browser we serve, and node ≥ 19);
 * a random-string fallback otherwise so a non-secure-context preview still
 * works rather than throwing halfway through "+ Add another animal".
 */
export function newAnimalId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  return `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** A blank animal row. */
export function newAnimalAddendumAnimal(id: string = newAnimalId()): AnimalAddendumDraftAnimal {
  return { id, ...blankAnimalAnswers() };
}

/**
 * The covered animals as the form's first rows: whatever the server knows is prefilled (name, kind,
 * breed, the other kind and its description), the rest left to the household. An 'other' with no further
 * kind opens its kind choice unanswered (the form asks). A name or kind the server gave is locked.
 */
export function coveredAnimalDrafts(
  covered: ReadonlyArray<AnimalAddendumCoveredAnimal> | undefined,
): AnimalAddendumDraftAnimal[] {
  return (covered ?? []).map((c) => {
    const answers = storedToAnimalAnswers({
      ...(c.name ? { name: c.name } : {}),
      ...(c.type ? { type: c.type } : {}),
      ...(c.otherKind ? { otherKind: c.otherKind } : {}),
      ...(c.otherDescription ? { otherDescription: c.otherDescription } : {}),
      ...(c.breed ? { breed: c.breed } : {}),
    });
    return {
      id: c.id,
      ...answers,
      covered: { lockName: answers.name.trim() !== '', lockKind: answers.kind !== '' },
    };
  });
}

/**
 * A draft with one blank animal — what the form opens on. With covered animals it opens on THEM
 * instead (they are the animals the household is here to tell us about); "+ Add another animal" adds
 * a normal one.
 */
export function newAnimalAddendumDraft(
  covered?: ReadonlyArray<AnimalAddendumCoveredAnimal>,
): AnimalAddendumDraft {
  const seeded = coveredAnimalDrafts(covered);
  return { animals: seeded.length > 0 ? seeded : [newAnimalAddendumAnimal()] };
}

/** Remove an animal from the draft. A covered animal is never removable, and the last animal stays. */
export function removeAnimalFromDraft(
  animals: ReadonlyArray<AnimalAddendumDraftAnimal>,
  id: string,
): AnimalAddendumDraftAnimal[] {
  const target = animals.find((a) => a.id === id);
  if (!target || target.covered !== undefined || animals.length <= 1) return [...animals];
  return animals.filter((a) => a.id !== id);
}

/** Whether the photo upload is offered for this animal (the matrix's photo row:
 *  dogs, cats and birds — and only once the kind is chosen). */
export function asksAddendumPhoto(a: Pick<AnimalAddendumDraftAnimal, 'kind'>): boolean {
  return asksDraftField(a, 'photo' satisfies AnimalField);
}

/**
 * Why this draft cannot be submitted yet, in words the tenant can act on
 * ("Tell us your dog's breed.", "How big is the tank, in gallons?"). Empty ⇒
 * ready. The form renders the first next to the (disabled) submit button.
 *
 * A missing license is deliberately NEVER a reason.
 */
export function animalAddendumReasons(d: AnimalAddendumDraft): string[] {
  if (d.animals.length === 0) return ['Add the animal you are bringing home.'];
  return animalAnswerReasons(d.animals);
}

/** True when the draft is complete enough to submit. */
export function canSubmitAnimalAddendum(d: AnimalAddendumDraft): boolean {
  return animalAddendumReasons(d).length === 0;
}

/**
 * Build the wire body from a (valid) draft. Returns null when the draft is
 * incomplete so a half-filled form can never be POSTed.
 *
 * Answers the chosen kind is not asked are DROPPED rather than sent: a cat
 * carries no breed and no weight even if the tenant typed one before switching
 * the kind, because the document prints exactly what arrives (and the API
 * drops them too).
 */
export function buildAnimalAddendumSubmission(
  d: AnimalAddendumDraft,
): AnimalAddendumSubmission | null {
  if (!canSubmitAnimalAddendum(d)) return null;
  const animals = d.animals.map((a): AnimalAddendumSubmissionAnimal => {
    const wire = draftToWireAnswers(a);
    // canSubmit guarantees a kind, so a type is always present here.
    return { id: a.id, ...wire, type: wire.type as AnimalAddendumAnimalType };
  });
  return { animals, questionsVersion: ANIMAL_QUESTIONS_VERSION };
}

/**
 * The animals named, for a sentence: "Mochi", "Mochi and Pip",
 * "Mochi, Pip and Juno". Unnamed rows are skipped rather than printed as a
 * placeholder — an empty result means the caller says something generic.
 *
 * NO Oxford comma, deliberately. This is the house party-list joiner, shared by
 * leaseholderChange, leaseholderRemoval, adoptionAddendum, moveoutFunds,
 * paymentPlanDoc, epmaDoc and the rental-verification emails — and the server
 * hands the signing page the same fragment built the same way. A household that
 * reads "Mochi, Pip and Kona" in its email must not read "Mochi, Pip, and Kona"
 * on the confirmation screen.
 */
export function animalNamesSentence(names: readonly string[]): string {
  const clean = names.map((n) => n.trim()).filter(Boolean);
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0] as string;
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}
